import * as React from 'react';

import {
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import {
  FIELD_NAME_ENUM,
  QUALIFIER_ENUM,
  PRIORITY_ENUM,
} from 'app/pages/DealerSegmentSettingsComponent/data/constants';
import {
  buildExistingSegmentComboKeys,
  buildSegmentComboKey,
  isSegmentComboComplete,
} from 'app/pages/DealerSegmentSettingsComponent/utils/dealerSegmentUniqueness';

export interface DealerSegmentRow {
  field_name: string;
  field_value: string;
  qualifier: string;
  primary_segment: string;
  secondary_segment: string;
  tertiary_segment: string;
  priority: number;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  maxWidth: '90vw',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  boxShadow: 24,
  p: 3,
  maxHeight: '90vh',
  overflow: 'auto',
};

const emptySegment: DealerSegmentRow = {
  field_name: '',
  field_value: '',
  qualifier: 'Starts with',
  primary_segment: '',
  secondary_segment: '',
  tertiary_segment: '',
  priority: 1,
};

export interface SegmentOptions {
  primary: string[];
  secondary: string[];
  tertiary: string[];
}

export type FieldValueByFieldName = Record<string, string[]>;

type ComboAvailability = 'incomplete' | 'available' | 'taken';

interface DealerSegmentModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initialValues?: DealerSegmentRow | null;
  onSave: (segment: DealerSegmentRow) => void;
  saving?: boolean;
  segmentOptions?: SegmentOptions;
  fieldValueByFieldName?: FieldValueByFieldName;
  /** All current segment rows — used for field_name + field_value + priority uniqueness. */
  existingSegments?: DealerSegmentRow[];
  /** When editing, exclude this index from the uniqueness check. */
  excludeIndex?: number;
}

export const DealerSegmentModal = ({
  open,
  onClose,
  mode,
  initialValues,
  onSave,
  saving,
  segmentOptions,
  fieldValueByFieldName = {},
  existingSegments = [],
  excludeIndex = -1,
}: DealerSegmentModalProps) => {
  const [form, setForm] = React.useState<DealerSegmentRow>(emptySegment);

  const primaryOptions = segmentOptions?.primary ?? [];
  const secondaryOptions = segmentOptions?.secondary ?? [];
  const tertiaryOptions = segmentOptions?.tertiary ?? [];

  const fieldValueOptions = React.useMemo(() => {
    if (!form.field_name) return [];
    const exact = fieldValueByFieldName[form.field_name];
    const fromApi =
      exact ??
      Object.entries(fieldValueByFieldName).find(
        ([k]) => k.trim().toLowerCase() === form.field_name.trim().toLowerCase(),
      )?.[1] ??
      [];
    return fromApi;
  }, [form.field_name, fieldValueByFieldName]);

  const existingComboKeys = React.useMemo(
    () => buildExistingSegmentComboKeys(existingSegments, excludeIndex),
    [existingSegments, excludeIndex],
  );

  const comboAvailability: ComboAvailability = React.useMemo(() => {
    if (!isSegmentComboComplete(form)) return 'incomplete';
    return existingComboKeys.has(buildSegmentComboKey(form))
      ? 'taken'
      : 'available';
  }, [form, existingComboKeys]);

  const comboHelperText =
    comboAvailability === 'available'
      ? 'Available — this Field Name, Field Value, and Priority combination is unique'
      : comboAvailability === 'taken'
        ? 'Already taken — this Field Name, Field Value, and Priority combination already exists'
        : undefined;

  React.useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialValues) {
        setForm({
          field_name: initialValues.field_name ?? '',
          field_value: initialValues.field_value ?? '',
          qualifier: initialValues.qualifier ?? 'Starts with',
          primary_segment: initialValues.primary_segment ?? '',
          secondary_segment: initialValues.secondary_segment ?? '',
          tertiary_segment: initialValues.tertiary_segment ?? '',
          priority: typeof initialValues.priority === 'number' ? initialValues.priority : 1,
        });
      } else {
        setForm(emptySegment);
      }
    }
  }, [open, mode, initialValues]);

  const handleSubmit = () => {
    if (comboAvailability !== 'available') return;
    onSave(form);
  };

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(JSON.stringify(form));
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = JSON.parse(text) as Partial<DealerSegmentRow>;
      if (parsed && typeof parsed === 'object') {
        setForm({
          field_name: String(parsed.field_name ?? ''),
          field_value: String(parsed.field_value ?? ''),
          qualifier: String(parsed.qualifier ?? 'Starts with'),
          primary_segment: String(parsed.primary_segment ?? ''),
          secondary_segment: String(parsed.secondary_segment ?? ''),
          tertiary_segment: String(parsed.tertiary_segment ?? ''),
          priority: typeof parsed.priority === 'number' ? parsed.priority : 1,
        });
      }
    } catch (e) {
      console.error('Paste failed', e);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="dealer-segment-modal">
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography id="dealer-segment-modal" variant="h6" component="h2">
            {mode === 'add' ? 'Add Configuration' : 'Edit Configuration'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Copy settings to clipboard">
              <IconButton onClick={handleCopy} size="small" aria-label="copy">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Paste settings from clipboard">
              <IconButton onClick={handlePaste} size="small" aria-label="paste">
                <ContentPasteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Stack spacing={2} sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            options={primaryOptions}
            value={form.primary_segment}
            onInputChange={(_e, value) =>
              setForm(prev => ({ ...prev, primary_segment: value ?? '' }))
            }
            onChange={(_e, value) =>
              setForm(prev => ({
                ...prev,
                primary_segment: typeof value === 'string' ? value : value ?? '',
              }))
            }
            renderInput={params => (
              <TextField {...params} label="Primary Segment" />
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Field Name</InputLabel>
            <Select
              value={form.field_name}
              label="Field Name"
              onChange={e => {
                const nextName = String(e.target.value);
                setForm(prev => ({
                  ...prev,
                  field_name: nextName,
                  field_value: prev.field_name !== nextName ? '' : prev.field_value,
                }));
              }}
            >
              {FIELD_NAME_ENUM.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            options={fieldValueOptions}
            value={form.field_value}
            onInputChange={(_e, value) =>
              setForm(prev => ({ ...prev, field_value: value ?? '' }))
            }
            onChange={(_e, value) =>
              setForm(prev => ({
                ...prev,
                field_value: typeof value === 'string' ? value : value ?? '',
              }))
            }
            renderInput={params => (
              <TextField {...params} label="Field Value" />
            )}
          />
          <FormControl fullWidth>
            <InputLabel>Qualifier</InputLabel>
            <Select
              value={form.qualifier}
              label="Qualifier"
              onChange={e =>
                setForm(prev => ({
                  ...prev,
                  qualifier: String(e.target.value),
                }))
              }
            >
              {QUALIFIER_ENUM.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            options={secondaryOptions}
            value={form.secondary_segment}
            onInputChange={(_e, value) =>
              setForm(prev => ({ ...prev, secondary_segment: value ?? '' }))
            }
            onChange={(_e, value) =>
              setForm(prev => ({
                ...prev,
                secondary_segment:
                  typeof value === 'string' ? value : value ?? '',
              }))
            }
            renderInput={params => (
              <TextField {...params} label="Secondary Segment" />
            )}
          />
          <Autocomplete
            freeSolo
            options={tertiaryOptions}
            value={form.tertiary_segment}
            onInputChange={(_e, value) =>
              setForm(prev => ({ ...prev, tertiary_segment: value ?? '' }))
            }
            onChange={(_e, value) =>
              setForm(prev => ({
                ...prev,
                tertiary_segment:
                  typeof value === 'string' ? value : value ?? '',
              }))
            }
            renderInput={params => (
              <TextField {...params} label="Tertiary Segment" />
            )}
          />
          <FormControl fullWidth error={comboAvailability === 'taken'}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={form.priority}
              label="Priority"
              onChange={e =>
                setForm(prev => ({
                  ...prev,
                  priority: Number(e.target.value) || 1,
                }))
              }
            >
              {PRIORITY_ENUM.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
            {comboHelperText && (
              <FormHelperText
                sx={{
                  color:
                    comboAvailability === 'available'
                      ? 'success.main'
                      : undefined,
                }}
              >
                {comboHelperText}
              </FormHelperText>
            )}
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || comboAvailability !== 'available'}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
